/**
 * Admin pricing-config API — leverage grids, rules, arrangements, coupon ladders,
 * adjustment tables, and the shared audit log.
 *
 * Ported from digital_sizer (pages/AdminGrids.tsx, pages/AdminRules.tsx,
 * components/admin/ChangeHistoryDrawer.tsx). Now wired onto the LOS client
 * (`dsHttp` → eastview-los), per the digital-sizer base-URL removal.
 *
 * ⚠️ TBD: eastview-os has NO `/admin/*` pricing-config routes yet (leverage
 * grids, rules, pricing arrangements, coupon ladders, adjustment tables, audit
 * log, restore). Every call below 404s until the backend adds an eastview-os
 * equivalent — point these at the real paths when they land.
 */
import { dsHttp } from "../lib/digitalSizerHttp";

export const adminPricingApi = {
  // ── Leverage grids ─────────────────────────────────────────────────────────
  // TBD (no eastview-os route): GET /admin/leverage-grids/:program
  getLeverageGrids: (program) =>
    dsHttp.get(`/admin/leverage-grids/${program}`).then((r) => r.data),

  // TBD (no eastview-os route): PUT /admin/leverage-grids/:program
  saveLeverageGrids: (program, rows, commitMessage) =>
    dsHttp
      .put(`/admin/leverage-grids/${program}`, {
        rows,
        commit_message: commitMessage || undefined,
      })
      .then((r) => r.data),

  // ── Rules ────────────────────────────────────────────────────────────────
  // TBD (no eastview-os route): GET /admin/rules/:type/:program
  getRules: (type, program) =>
    dsHttp.get(`/admin/rules/${type}/${program}`).then((r) => r.data),

  // TBD (no eastview-os route): PUT /admin/rules/:type/:program
  saveRules: (type, program, rows, commitMessage) =>
    dsHttp
      .put(`/admin/rules/${type}/${program}`, {
        rows,
        commit_message: commitMessage || undefined,
      })
      .then((r) => r.data),

  // ── Pricing arrangements (master/detail) ─────────────────────────────────
  // TBD (no eastview-os route): GET /admin/pricing-arrangements
  getPricingArrangements: () =>
    dsHttp.get("/admin/pricing-arrangements").then((r) => r.data),

  // TBD (no eastview-os route): POST /admin/pricing-arrangements
  createPricingArrangement: (body) =>
    dsHttp.post("/admin/pricing-arrangements", body).then((r) => r.data),

  // TBD (no eastview-os route): PUT /admin/pricing-arrangements
  savePricingArrangement: (arrangement, commitMessage) =>
    dsHttp
      .put("/admin/pricing-arrangements", {
        ...arrangement,
        commit_message: commitMessage || undefined,
      })
      .then((r) => r.data),

  // ── Coupon ladders (per program) ─────────────────────────────────────────
  // TBD (no eastview-os route): GET /admin/coupon-ladders/:program
  getCouponLadder: (program) =>
    dsHttp.get(`/admin/coupon-ladders/${program}`).then((r) => r.data),

  // TBD (no eastview-os route): PUT /admin/coupon-ladders/:program
  saveCouponLadder: (program, rows, commitMessage) =>
    dsHttp
      .put(`/admin/coupon-ladders/${program}`, {
        rows,
        commit_message: commitMessage || undefined,
      })
      .then((r) => r.data),

  // TBD (no eastview-os route): POST /admin/coupon-ladders/:program/bulk-shift
  bulkShiftCouponLadder: (program, body) =>
    dsHttp.post(`/admin/coupon-ladders/${program}/bulk-shift`, body).then((r) => r.data),

  // TBD (no eastview-os route): POST /admin/coupon-ladders/:program/lock
  lockCouponLadder: (program, note) =>
    dsHttp.post(`/admin/coupon-ladders/${program}/lock`, { note }).then((r) => r.data),

  // ── Adjustment tables (per tableKey) ─────────────────────────────────────
  // TBD (no eastview-os route): GET /admin/adjustment-tables/:tableKey
  getAdjustmentTable: (tableKey) =>
    dsHttp.get(`/admin/adjustment-tables/${tableKey}`).then((r) => r.data),

  // TBD (no eastview-os route): PUT /admin/adjustment-tables/:tableKey
  saveAdjustmentTable: (tableKey, data, commitMessage) =>
    dsHttp
      .put(`/admin/adjustment-tables/${tableKey}`, {
        data,
        commit_message: commitMessage || undefined,
      })
      .then((r) => r.data),

  // TBD (no eastview-os route): POST /admin/adjustment-tables/:tableKey/bulk-shift
  bulkShiftAdjustmentTable: (tableKey, delta) =>
    dsHttp.post(`/admin/adjustment-tables/${tableKey}/bulk-shift`, { delta }).then((r) => r.data),

  // TBD (no eastview-os route): POST /admin/adjustment-tables/:tableKey/lock
  lockAdjustmentTable: (tableKey, note) =>
    dsHttp.post(`/admin/adjustment-tables/${tableKey}/lock`, { note }).then((r) => r.data),

  // ── Audit log (Change History) ───────────────────────────────────────────
  // TBD (no eastview-os route): GET /admin/audit-log
  getAuditLog: (entityType, entityId) =>
    dsHttp
      .get("/admin/audit-log", {
        params: { entity_type: entityType, entity_id: entityId, limit: 100 },
      })
      .then((r) => r.data),

  // TBD (no eastview-os route): POST /admin/:entity_type/:entity_id/restore/:version
  restoreVersion: (entityType, entityId, version, commitMessage) =>
    dsHttp
      .post(
        `/admin/${entityType}/${encodeURIComponent(entityId)}/restore/${version}`,
        { commit_message: commitMessage || `restore from v${version}` },
      )
      .then((r) => r.data),
};

export default adminPricingApi;
